import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Typography,
  Stack,
  InputBase,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { keyframes } from "@mui/system";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import ViewStreamRoundedIcon from "@mui/icons-material/ViewStreamRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import MedicalServicesRoundedIcon from "@mui/icons-material/MedicalServicesRounded";

import Dialog from "../../../components/dialog/Dialog";
import Content from "../../../components/Content";
import { DocumentEditModal } from "../../../components/document/DocumentEditModal";
import {
  GetDocumentByOrganisationId,
  UploadDocument,
} from "../../../hooks/useDocumentData";
import { documentGroups } from "../../../constants/DocumentGroups";
import { CATEGORY_META, metaForKey } from "../../../constants/emergencyCategories";

const floatOrb = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(-14px, 20px, 0) scale(1.04); }
`;

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const getExtension = (filename = "") => {
  const m = filename.match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toUpperCase() : "FIL";
};

const getDisplayName = (filename = "") => {
  if (!filename) return "Okänd fil";
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
};

const FileTile = ({ filename, categoryKey, size = 52 }) => {
  const meta = metaForKey(categoryKey);
  const ext = getExtension(filename);
  const extLength = ext.length;
  return (
    <Box
      sx={{
        width: size,
        height: size * 1.2,
        minWidth: size,
        borderRadius: "12px",
        background: `linear-gradient(155deg, ${meta.color} 0%, ${meta.dark} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 1px 0 rgba(255,255,255,0.35) inset, 0 12px 26px -14px ${meta.color}88`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: size * 0.26,
          height: size * 0.26,
          background: "rgba(255,255,255,0.18)",
          clipPath: "polygon(0 0, 100% 100%, 100% 0)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "12px",
          background:
            "radial-gradient(circle at 20% 15%, rgba(255,255,255,0.22), transparent 55%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: extLength > 3 ? size * 0.22 : size * 0.28,
          letterSpacing: "-0.01em",
          color: "#fff",
          mb: size * 0.18 + "px",
          position: "relative",
          zIndex: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        {ext}
      </Typography>
    </Box>
  );
};

const CategoryChip = ({ categoryKey, compact = false }) => {
  const meta = metaForKey(categoryKey);
  const Icon = meta.icon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${meta.border}`,
        background: meta.tint,
        color: meta.color,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.005em",
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: meta.color,
          boxShadow: `0 0 0 4px ${meta.halo}`,
          flexShrink: 0,
        }}
      />
      <Icon sx={{ fontSize: 16, opacity: 0.95, flexShrink: 0 }} />
      {compact ? meta.short : meta.label}
    </Box>
  );
};

const MetricCell = ({ label, value, total, tint, index, icon: Icon }) => {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  return (
    <Box
      sx={{
        py: 3.5,
        pr: 3,
        pl: { xs: 0, md: index === 0 ? 0 : 3 },
        position: "relative",
        borderRight: {
          xs: "none",
          md: index < 3 ? `1px solid ${LINE}` : "none",
        },
        borderBottom: {
          xs: index < 2 ? `1px solid ${LINE}` : "none",
          md: "none",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {Icon && <Icon sx={{ fontSize: 13, color: INK_MUTED, opacity: 0.8 }} />}
        <Typography
          sx={{
            fontSize: "10.5px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: INK_MUTED,
            fontFamily: '"DM Sans", sans-serif',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: "2.75rem",
            fontWeight: 500,
            lineHeight: 0.95,
            letterSpacing: "-0.035em",
            color: INK,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </Typography>
        {total > 0 && index > 0 && (
          <Typography
            sx={{
              fontSize: "12px",
              color: INK_MUTED,
              fontWeight: 500,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            / {total}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "2px",
          width: "100%",
          maxWidth: 180,
          background: LINE_SOFT,
          borderRadius: "2px",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: tint,
            transformOrigin: "left",
            transform: `scaleX(${pct})`,
          }}
        />
      </Box>
    </Box>
  );
};

const DocumentRow = ({ document, onSelect }) => (
  <Box
    onClick={() => onSelect(document.id)}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "auto 1fr auto",
        md: "auto minmax(0, 1.8fr) minmax(0, 1fr) auto",
      },
      alignItems: "center",
      gap: { xs: 2, md: 3 },
      px: 3,
      py: 2.25,
      cursor: "pointer",
      position: "relative",
      borderBottom: `1px solid ${LINE_SOFT}`,
      transition: "background 0.3s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "16%",
        bottom: "16%",
        width: "2px",
        background: BRAND,
        transform: "scaleY(0)",
        transformOrigin: "center",
        transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        background: "rgba(138, 17, 37, 0.025)",
        "&::before": { transform: "scaleY(1)" },
        "& .row-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "& .row-name": { color: BRAND },
      },
    }}
  >
    <FileTile
      filename={document.filename}
      categoryKey={document.document_group_key}
      size={44}
    />
    <Box sx={{ minWidth: 0 }}>
      <Typography
        className="row-name"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontSize: "16.5px",
          fontWeight: 600,
          color: INK,
          letterSpacing: "-0.01em",
          lineHeight: 1.25,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          transition: "color 0.25s ease",
        }}
      >
        {getDisplayName(document.filename)}
      </Typography>
      <Typography
        sx={{
          fontSize: "12.5px",
          color: INK_MUTED,
          mt: 0.25,
          fontFamily: '"DM Sans", sans-serif',
          letterSpacing: "0.01em",
        }}
      >
        {getExtension(document.filename)}-dokument
      </Typography>
    </Box>
    <Box sx={{ display: { xs: "none", md: "flex" } }}>
      <CategoryChip categoryKey={document.document_group_key} />
    </Box>
    <Box
      className="row-arrow"
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        border: `1px solid ${LINE}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0,
        transform: "translateX(-6px)",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        color: BRAND,
      }}
    >
      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
    </Box>
  </Box>
);

const DocumentCard = ({ document, onSelect }) => (
  <Box
    onClick={() => onSelect(document.id)}
    sx={{
      position: "relative",
      p: 3.25,
      background: "#fff",
      border: `1px solid ${LINE}`,
      borderRadius: "16px",
      cursor: "pointer",
      overflow: "hidden",
      transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        width: "48px",
        height: "2px",
        background: BRAND,
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      },
      "&:hover": {
        borderColor: "rgba(10,22,40,0.18)",
        transform: "translateY(-3px)",
        boxShadow:
          "0 24px 48px -24px rgba(10,22,40,0.16), 0 2px 6px -2px rgba(10,22,40,0.04)",
        "&::before": { transform: "scaleX(1)" },
        "& .card-arrow": { transform: "translateX(3px)", color: BRAND },
      },
    }}
  >
    <Box sx={{ mb: 3 }}>
      <FileTile
        filename={document.filename}
        categoryKey={document.document_group_key}
        size={58}
      />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "19px",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.02em",
        lineHeight: 1.22,
        mb: 0.75,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        minHeight: "47px",
        wordBreak: "break-word",
      }}
    >
      {getDisplayName(document.filename)}
    </Typography>
    <Typography
      sx={{
        fontSize: "12.5px",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
        mb: 2.5,
      }}
    >
      {getExtension(document.filename)}-dokument
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
      <CategoryChip categoryKey={document.document_group_key} />
    </Stack>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 2.25,
        borderTop: `1px solid ${LINE_SOFT}`,
      }}
    >
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: INK_MUTED,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontFamily: '"DM Sans", sans-serif',
        }}
      >
        Redigera
      </Typography>
      <ArrowForwardRoundedIcon
        className="card-arrow"
        sx={{
          fontSize: 18,
          color: INK_MUTED,
          transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </Box>
  </Box>
);

const EmptyState = ({ onUpload, hasQuery }) => (
  <Box
    sx={{
      py: 10,
      textAlign: "center",
      border: `1px dashed ${LINE}`,
      borderRadius: "16px",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
    }}
  >
    <Box
      sx={{
        width: 68,
        height: 68,
        mx: "auto",
        mb: 3,
        borderRadius: "20px",
        background: `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 20px 40px -20px rgba(16,50,74,0.4)",
      }}
    >
      <DescriptionRoundedIcon sx={{ color: "#fff", fontSize: 32 }} />
    </Box>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "1.75rem",
        fontWeight: 600,
        color: INK,
        letterSpacing: "-0.025em",
        mb: 1,
      }}
    >
      {hasQuery ? "Inga träffar" : "Inga dokument ännu"}
    </Typography>
    <Typography
      sx={{
        fontSize: "14.5px",
        color: INK_MUTED,
        maxWidth: 460,
        mx: "auto",
        mb: 4,
        lineHeight: 1.6,
      }}
    >
      {hasQuery
        ? "Prova en annan sökterm eller rensa filtren för att se alla dokument."
        : "Ladda upp era beredskapsdokument och organisera dem per krisscenario — dödligt våld, bombhot, brandlarm, olycka eller övrigt."}
    </Typography>
    {!hasQuery && (
      <Box
        component="button"
        type="button"
        onClick={onUpload}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          px: 3,
          py: 1.5,
          background: BRAND,
          color: "#fff",
          border: "none",
          borderRadius: "10px",
          fontFamily: '"DM Sans", sans-serif',
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 0.25s ease",
          boxShadow: "0 10px 24px -12px rgba(138,17,37,0.5)",
          "&:hover": {
            background: "#6b0d1d",
            transform: "translateY(-1px)",
          },
        }}
      >
        <FileUploadRoundedIcon sx={{ fontSize: 18 }} />
        Ladda upp dokument
      </Box>
    )}
  </Box>
);

const selectSx = {
  height: 44,
  minWidth: 200,
  background: "#fff",
  fontSize: "13px",
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  fontWeight: 600,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
};

const menuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "13px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.1,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

export const DocumentView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);

  const [organisationId, setOrganisationId] = useState("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editDocumentId, setEditDocumentId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(documentGroups[0].key);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const { organisation_id } = JSON.parse(userData);
        setOrganisationId(organisation_id);
      }
    } catch (e) {
      console.error("Error getting organisation id:", e);
    }
  }, []);

  const { data, isLoading } = GetDocumentByOrganisationId(organisationId);

  const documents = useMemo(() => {
    const raw = Array.isArray(data?.data) ? data.data : [];
    return raw.map((d) => ({
      id: d.id,
      filename: d.filename,
      document_group_key: d.document_group,
      document_group_label: metaForKey(d.document_group).label,
    }));
  }, [data]);

  const metrics = useMemo(() => {
    const byKey = documents.reduce((acc, d) => {
      acc[d.document_group_key] = (acc[d.document_group_key] || 0) + 1;
      return acc;
    }, {});
    return {
      total: documents.length,
      pdv: byKey.pdv || 0,
      brand: byKey.brand || 0,
      olycka: byKey.olycka || 0,
    };
  }, [documents]);

  const filteredDocuments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return documents.filter((d) => {
      if (q && !(d.filename || "").toLowerCase().includes(q)) return false;
      if (categoryFilter !== "all" && d.document_group_key !== categoryFilter)
        return false;
      return true;
    });
  }, [documents, search, categoryFilter]);

  const uploadDocumentMutation = UploadDocument({
    onSuccess: () => {
      enqueueSnackbar("Dokument uppladdat.", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Dokument kunde inte laddas upp.", { variant: "error" });
    },
  });

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedGroup(documentGroups[0].key);
      setIsUploadModalOpen(true);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      enqueueSnackbar("Ingen fil vald.", { variant: "warning" });
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("organisation_id", organisationId);
    formData.append("document_group", selectedGroup);
    formData.append("original_filename", selectedFile.name);
    uploadDocumentMutation.mutate(formData);
    setIsUploadModalOpen(false);
    setSelectedFile(null);
  };

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedFile(null);
  };

  const hasActiveQuery = !!search.trim() || categoryFilter !== "all";
  const uploading = uploadDocumentMutation.isLoading;

  return (
    <Content>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <Box
        sx={{
          position: "relative",
          px: { xs: 1, md: 2 },
          pt: 2,
          pb: 6,
          minHeight: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            right: -80,
            width: 520,
            height: 520,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(138,17,37,0.09), rgba(138,17,37,0) 62%)",
            filter: "blur(8px)",
            pointerEvents: "none",
            animation: `${floatOrb} 14s ease-in-out infinite`,
            zIndex: 0,
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 80,
            left: -140,
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 50% 50%, rgba(16,50,74,0.07), rgba(16,50,74,0) 62%)",
            filter: "blur(10px)",
            pointerEvents: "none",
            animation: `${floatOrb} 18s ease-in-out infinite reverse`,
            zIndex: 0,
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1, mb: 5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: "1.5px",
                background: `linear-gradient(90deg, ${BRAND}, rgba(138,17,37,0))`,
              }}
            />
            <Typography
              sx={{
                fontSize: "10.5px",
                fontWeight: 700,
                letterSpacing: "0.26em",
                textTransform: "uppercase",
                color: BRAND,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Organisation · Krisdokumentation
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.5rem", md: "3.75rem" },
                  lineHeight: 0.98,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 2.5,
                }}
              >
                Dokument
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontWeight: 300,
                    fontStyle: "italic",
                    fontSize: "0.58em",
                    color: BRAND,
                    mt: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  & beredskap
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "1rem",
                  lineHeight: 1.7,
                  color: INK_MUTED,
                  maxWidth: 560,
                }}
              >
                Samla era handlingsplaner på ett ställe. Ladda upp riktlinjer
                och rutiner per krisscenario — dödligt våld, bombhot, brandlarm,
                olycka eller övrigt — så att rätt dokument finns nära till hands
                när det verkligen gäller.
              </Typography>
            </Box>

            <Box
              component="button"
              type="button"
              onClick={openFilePicker}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.25,
                px: 2.75,
                py: 1.625,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.005em",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                boxShadow:
                  "0 14px 32px -14px rgba(10,22,40,0.45), 0 2px 4px -2px rgba(10,22,40,0.08)",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(135deg, ${BRAND}, transparent 70%)`,
                  opacity: 0,
                  transition: "opacity 0.3s ease",
                },
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 22px 44px -18px rgba(10,22,40,0.55), 0 2px 6px -2px rgba(10,22,40,0.12)",
                  "&::before": { opacity: 1 },
                },
                "& > *": { position: "relative", zIndex: 1 },
              }}
            >
              <FileUploadRoundedIcon sx={{ fontSize: 18 }} />
              Ladda upp dokument
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              borderTop: `1px solid ${LINE}`,
              borderBottom: `1px solid ${LINE}`,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.5), rgba(255,255,255,0))",
              backdropFilter: "blur(4px)",
            }}
          >
            <MetricCell
              label="Totalt antal dokument"
              value={metrics.total}
              total={metrics.total}
              tint={INK}
              index={0}
            />
            <MetricCell
              label="Dödligt våld"
              value={metrics.pdv}
              total={metrics.total}
              tint={CATEGORY_META.pdv.color}
              index={1}
              icon={ShieldRoundedIcon}
            />
            <MetricCell
              label="Brandlarm"
              value={metrics.brand}
              total={metrics.total}
              tint={CATEGORY_META.brand.color}
              index={2}
              icon={LocalFireDepartmentRoundedIcon}
            />
            <MetricCell
              label="Olycka"
              value={metrics.olycka}
              total={metrics.total}
              tint={CATEGORY_META.olycka.color}
              index={3}
              icon={MedicalServicesRoundedIcon}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 2,
              height: 44,
              minWidth: 260,
              flex: { xs: "1 1 100%", md: "0 1 320px" },
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              transition: "all 0.25s ease",
              "&:focus-within": {
                borderColor: "rgba(10,22,40,0.28)",
                boxShadow: "0 0 0 4px rgba(10,22,40,0.04)",
              },
            }}
          >
            <SearchRoundedIcon sx={{ fontSize: 18, color: INK_MUTED }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Sök dokument..."
              sx={{
                flex: 1,
                fontSize: "14px",
                fontFamily: '"DM Sans", sans-serif',
                color: INK,
                "& input::placeholder": {
                  color: INK_MUTED,
                  opacity: 1,
                },
              }}
            />
          </Box>

          <FormControl>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              IconComponent={KeyboardArrowDownRoundedIcon}
              MenuProps={menuProps}
              renderValue={(v) => (
                <>
                  <FilterAltRoundedIcon
                    sx={{ fontSize: 15, color: INK_MUTED }}
                  />
                  {v === "all" ? "Alla kategorier" : metaForKey(v).label}
                </>
              )}
              sx={selectSx}
            >
              <MenuItem value="all">Alla kategorier</MenuItem>
              {documentGroups.map((g) => (
                <MenuItem key={g.key} value={g.key}>
                  {g.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              background: "#fff",
              border: `1px solid ${LINE}`,
              borderRadius: "12px",
              p: "3px",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "9px !important",
                px: 1.5,
                py: 0.75,
                color: INK_MUTED,
                transition: "all 0.25s ease",
                "&.Mui-selected": {
                  background: INK,
                  color: "#fff",
                  "&:hover": { background: INK },
                },
              },
            }}
          >
            <ToggleButton value="list" aria-label="list view">
              <ViewStreamRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewRoundedIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <FilterAltRoundedIcon sx={{ fontSize: 14, color: INK_MUTED }} />
          <Typography
            sx={{
              fontSize: "11.5px",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {isLoading
              ? "Läser in"
              : `Visar ${filteredDocuments.length} dokument${
                  hasActiveQuery ? ` av ${documents.length}` : ""
                }`}
          </Typography>
          <Box sx={{ flex: 1, height: "1px", background: LINE }} />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          {isLoading ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    px: 3,
                    py: 2.5,
                    borderBottom: i < 3 ? `1px solid ${LINE_SOFT}` : "none",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={44}
                    height={52}
                    sx={{ borderRadius: "12px" }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="52%" height={20} />
                    <Skeleton width="22%" height={14} sx={{ mt: 0.5 }} />
                  </Box>
                  <Skeleton
                    variant="rounded"
                    width={150}
                    height={32}
                    sx={{ borderRadius: "999px" }}
                  />
                </Box>
              ))}
            </Box>
          ) : filteredDocuments.length === 0 ? (
            <EmptyState
              onUpload={openFilePicker}
              hasQuery={hasActiveQuery}
            />
          ) : viewMode === "list" ? (
            <Box
              sx={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 1px 2px rgba(10,22,40,0.02)",
                "& > *:last-child": { borderBottom: "none" },
              }}
            >
              {filteredDocuments.map((document) => (
                <DocumentRow
                  key={document.id}
                  document={document}
                  onSelect={setEditDocumentId}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2.5,
              }}
            >
              {filteredDocuments.map((document) => (
                <DocumentCard
                  key={document.id}
                  document={document}
                  onSelect={setEditDocumentId}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        title="Ladda upp dokument"
        open={isUploadModalOpen}
        onClose={closeUploadModal}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2.5,
              mb: 4,
              pb: 3,
              borderBottom: `1px solid ${LINE_SOFT}`,
            }}
          >
            <FileTile
              filename={selectedFile?.name || ""}
              categoryKey={selectedGroup}
              size={60}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                  mb: 0.5,
                }}
              >
                Vald fil
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: "1.375rem",
                  fontWeight: 600,
                  color: INK,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  wordBreak: "break-all",
                }}
              >
                {selectedFile?.name || "Ingen fil vald"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontSize: "11px",
                  fontWeight: 600,
                  color: BRAND,
                  letterSpacing: "0.1em",
                }}
              >
                01
              </Typography>
              <Box sx={{ width: 14, height: "1px", background: LINE }} />
              <Typography
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 700,
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                }}
              >
                Välj kategori
              </Typography>
            </Box>
            <Stack spacing={1.25}>
              {documentGroups.map((g) => {
                const meta = metaForKey(g.key);
                const Icon = meta.icon;
                const active = selectedGroup === g.key;
                return (
                  <Box
                    key={g.key}
                    component="button"
                    type="button"
                    onClick={() => setSelectedGroup(g.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.75,
                      width: "100%",
                      textAlign: "left",
                      cursor: "pointer",
                      background: active ? meta.tint : "#fff",
                      border: `1px solid ${active ? meta.border : LINE}`,
                      borderRadius: "12px",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
                      fontFamily: '"DM Sans", sans-serif',
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        background: meta.color,
                        transform: active ? "scaleY(1)" : "scaleY(0)",
                        transformOrigin: "center",
                        transition:
                          "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
                      },
                      "&:hover": {
                        borderColor: active
                          ? meta.border
                          : "rgba(10,22,40,0.2)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        minWidth: 40,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: active
                          ? `linear-gradient(135deg, ${meta.color}, ${meta.dark})`
                          : "#f4f2ee",
                        color: active ? "#fff" : INK_MUTED,
                        transition: "all 0.35s ease",
                        boxShadow: active
                          ? `0 10px 22px -10px ${meta.color}80`
                          : "none",
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }} />
                    </Box>
                    <Typography
                      sx={{
                        flex: 1,
                        fontFamily: '"Outfit", sans-serif',
                        fontWeight: 600,
                        fontSize: "15px",
                        color: active ? meta.color : INK,
                        letterSpacing: "-0.005em",
                      }}
                    >
                      {g.label}
                    </Typography>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        border: `1.5px solid ${
                          active ? meta.color : "rgba(10,22,40,0.18)"
                        }`,
                        background: active ? meta.color : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.25s ease",
                        flexShrink: 0,
                      }}
                    >
                      {active && (
                        <CheckRoundedIcon
                          sx={{ fontSize: 13, color: "#fff" }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.25,
              pt: 3,
              borderTop: `1px solid ${LINE_SOFT}`,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={closeUploadModal}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.25,
                py: 1.25,
                background: "transparent",
                color: INK_MUTED,
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.25s ease",
                "&:hover": {
                  color: INK,
                  borderColor: "rgba(10,22,40,0.22)",
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
              Avbryt
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2.75,
                py: 1.25,
                background: INK,
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "13px",
                fontWeight: 600,
                cursor: !selectedFile || uploading ? "not-allowed" : "pointer",
                opacity: !selectedFile || uploading ? 0.4 : 1,
                transition: "all 0.25s ease",
                boxShadow:
                  !selectedFile || uploading
                    ? "none"
                    : "0 10px 24px -12px rgba(10,22,40,0.55)",
                "&:hover":
                  !selectedFile || uploading
                    ? {}
                    : {
                        background: "#1a2638",
                        transform: "translateY(-1px)",
                      },
              }}
            >
              {uploading ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                <FileUploadRoundedIcon sx={{ fontSize: 16 }} />
              )}
              Ladda upp
            </Box>
          </Box>
        </Box>
      </Dialog>

      {editDocumentId && (
        <Dialog
          title="Redigera dokument"
          open={!!editDocumentId}
          onClose={() => setEditDocumentId(null)}
        >
          <DocumentEditModal
            documentId={editDocumentId}
            onClose={() => setEditDocumentId(null)}
          />
        </Dialog>
      )}
    </Content>
  );
};

export default DocumentView;
